import { useEffect, useMemo, useRef, useState } from "react";
import { api, API, fmtDate } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
    Plus, Eye, Trash2, Star, Palette, Loader2, RefreshCw, FileUp, Wand2,
    LayoutTemplate, FileText, ListChecks, Calculator, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const STYLES = [
    { v: "header_bar", l: "Header Bar", d: "Dark banner across top with TAX INVOICE centered + logo left, org details right" },
    { v: "modern", l: "Modern", d: "Bold accent BALANCE DUE card · spacious · default" },
    { v: "classic", l: "Classic", d: "Centered title · serif headers · ledger feel" },
    { v: "compact", l: "Compact", d: "Dense tables · ideal for multi-page invoices" },
    { v: "minimal", l: "Minimal", d: "No accent color · pure black & white · quiet" },
];

const PALETTES = [
    { name: "Navy & Gold", primary: "#13192e", accent: "#a68a56" },
    { name: "Onyx",        primary: "#1a1a1a", accent: "#1a1a1a" },
    { name: "Crimson",     primary: "#1a1a1a", accent: "#b1352b" },
    { name: "Forest",      primary: "#163d2c", accent: "#c4a468" },
    { name: "Indigo",      primary: "#2a2a72", accent: "#7bdff2" },
    { name: "Mauve",       primary: "#3a2a3f", accent: "#d1a3a4" },
    { name: "Slate",       primary: "#334155", accent: "#f59e0b" },
    { name: "Royal Blue",  primary: "#0b2950", accent: "#1d6fb8" },
];

const FONT_FAMILIES = [
    { v: "DejaVu",   l: "DejaVu Sans (₹ ready)" },
    { v: "Helvetica",l: "Helvetica" },
    { v: "Times",    l: "Times (Serif)" },
];

const PAPER_SIZES = ["A4", "A5", "Letter"];

const EMPTY = {
    name: "New Template",
    template_style: "modern",
    header_style: "modern",
    primary_color: "#13192e",
    accent_color: "#a68a56",
    show_logo: true,
    set_as_default: false,

    // General
    paper_size: "A4",
    orientation: "portrait",
    margin_top: 12, margin_bottom: 12, margin_left: 14, margin_right: 14,
    font_family: "DejaVu",
    body_font_size: 9.5,
    bg_color: "",

    // Header
    header_title: "TAX INVOICE",
    header_title_align: "center",
    header_bar_color: "#1a1a1a",
    header_text_color: "#ffffff",
    logo_position: "left",
    logo_size_mm: 60,
    show_org_in_header: true,

    // Footer
    footer_text: "",
    show_page_numbers: false,

    // Table
    show_col_hsn: true,
    show_col_qty: true,
    show_col_rate: true,
    show_col_tax: true,
    show_col_discount: false,
    show_description: true,
    alternating_rows: false,

    // Totals
    show_total_subtotal: true,
    show_total_discount: true,
    show_total_taxes: true,
    show_total_shipping: true,
    show_total_roundoff: true,
    show_total_paid: true,

    // Labels
    label_invoice_no: "Invoice #",
    label_invoice_date: "Invoice Date",
    label_due_date: "Due Date",
    label_terms: "Terms",
    label_balance_due: "BALANCE DUE",
    label_total: "Total",
    label_subtotal: "Sub Total",
    label_bill_to: "BILL TO",
    label_ship_to: "SHIP TO",

    // Other
    show_notes: true,
    show_terms: true,
    show_signature: true,
    signature_label: "Authorized Signatory",
    show_e_invoice_block: true,
    show_amount_in_words: true,
    show_bank_details: false,
    bank_details: "",
    terms_template: "Payment due as per terms. Late payments may attract interest @1.5% per month.",
};

const TABS = [
    { v: "general",      l: "General",            icon: LayoutTemplate },
    { v: "header",       l: "Header & Footer",    icon: FileText },
    { v: "transaction",  l: "Transaction Details",icon: ListChecks },
    { v: "table",        l: "Table",              icon: ListChecks },
    { v: "totals",       l: "Totals",             icon: Calculator },
    { v: "other",        l: "Other Details",      icon: Sparkles },
];

export default function InvoiceTemplates() {
    const [items, setItems] = useState([]);
    const [designer, setDesigner] = useState({ open: false, item: null });
    const [thumbs, setThumbs] = useState({});
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/invoice-templates"); setItems(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        const token = localStorage.getItem("vyastra_token");
        items.forEach((t) => {
            if (thumbs[t.id]) return;
            fetch(`${API}/invoice-templates/${t.id}/preview-pdf`, { method: "POST", headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.blob())
                .then((b) => setThumbs((s) => ({ ...s, [t.id]: URL.createObjectURL(b) })))
                .catch(() => {});
        });
    }, [items]); // eslint-disable-line

    const setDefault = async (it) => {
        await api.put(`/invoice-templates/${it.id}`, { ...it, set_as_default: true });
        toast.success(`"${it.name}" is now default`);
        await load();
    };

    const remove = async (id) => {
        if (!confirm("Delete this template?")) return;
        await api.delete(`/invoice-templates/${id}`);
        toast.success("Template deleted");
        await load();
    };

    const previewPdf = (it) => {
        const win = window.open("", "_blank");
        const token = localStorage.getItem("vyastra_token");
        fetch(`${API}/invoice-templates/${it.id}/preview-pdf`, { method: "POST", headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => {
                const u = URL.createObjectURL(b);
                if (win && !win.closed) win.location.href = u;
                else { const a = document.createElement("a"); a.href = u; a.target = "_blank"; document.body.appendChild(a); a.click(); a.remove(); }
            });
    };

    return (
        <>
            <PageHeader
                kicker="SETTINGS • DESIGN"
                title="Invoice Templates"
                subtitle="Design and preview your invoice PDFs side-by-side. Set one as default and override per invoice."
                actions={<Button onClick={() => setDesigner({ open: true, item: null })} data-testid="template-new"><Plus className="w-4 h-4 mr-1.5" />New template</Button>}
            />
            <PageBody>
                {loading ? (
                    <div className="text-center text-muted-foreground py-12 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading templates…</div>
                ) : items.length === 0 ? (
                    <div className="border border-dashed border-border rounded-md p-10 text-center text-muted-foreground bg-card">
                        <Palette className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm mb-3">No templates yet. The default template is used until you create your own.</p>
                        <Button onClick={() => setDesigner({ open: true, item: null })} variant="outline"><Plus className="w-4 h-4 mr-1.5" />Create your first template</Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((t) => (
                            <div key={t.id} className={`group bg-card border rounded-md overflow-hidden hover:shadow-md transition ${t.is_default ? "border-[#a68a56]/60" : "border-border"}`} data-testid={`template-card-${t.id}`}>
                                <div className="aspect-[1/1.3] bg-muted/40 border-b border-border relative overflow-hidden">
                                    {thumbs[t.id] ? (
                                        <iframe src={`${thumbs[t.id]}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} title={t.name} className="w-full h-full pointer-events-none" data-testid={`template-thumb-${t.id}`} />
                                    ) : (
                                        <div className="absolute inset-0 grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
                                    )}
                                    <div className="absolute inset-0 cursor-pointer" onClick={() => previewPdf(t)} title="Open full preview" />
                                    {t.is_default && (
                                        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#a68a56] text-white text-[10px] uppercase tracking-[0.16em] font-semibold flex items-center gap-1"><Star className="w-3 h-3 fill-white" /> Default</span>
                                    )}
                                </div>
                                <div className="p-3">
                                    <div className="flex items-baseline justify-between mb-1">
                                        <h3 className="font-semibold text-[14px] truncate">{t.name}</h3>
                                        <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{t.header_style || t.template_style}</span>
                                    </div>
                                    <div className="flex gap-1 mb-3">
                                        <span className="w-5 h-5 rounded-full border border-border" style={{ backgroundColor: t.primary_color }} title={`Primary ${t.primary_color}`} />
                                        <span className="w-5 h-5 rounded-full border border-border" style={{ backgroundColor: t.accent_color }} title={`Accent ${t.accent_color}`} />
                                        <span className="text-[11px] text-muted-foreground ml-1">{fmtDate(t.created_at)}</span>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button size="sm" variant="outline" className="flex-1" onClick={() => setDesigner({ open: true, item: t })} data-testid={`template-edit-${t.id}`}>Edit</Button>
                                        <Button size="sm" variant="ghost" onClick={() => previewPdf(t)} data-testid={`template-preview-${t.id}`} title="Preview"><Eye className="w-3.5 h-3.5" /></Button>
                                        {!t.is_default && <Button size="sm" variant="ghost" onClick={() => setDefault(t)} title="Set as default"><Star className="w-3.5 h-3.5" /></Button>}
                                        <Button size="sm" variant="ghost" onClick={() => remove(t.id)} title="Delete"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </PageBody>

            <Designer
                open={designer.open}
                item={designer.item}
                onClose={() => setDesigner({ open: false, item: null })}
                onSaved={async () => { setDesigner({ open: false, item: null }); setThumbs({}); await load(); }}
            />
        </>
    );
}

function Designer({ open, item, onClose, onSaved }) {
    const [form, setForm] = useState(EMPTY);
    const [tab, setTab] = useState("general");
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewBusy, setPreviewBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const debounceRef = useRef(null);
    const lastUrlRef = useRef("");
    const importInputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        // Backwards-compat: if older template only has template_style, mirror to header_style
        const seed = item ? { ...EMPTY, ...item, header_style: item.header_style || item.template_style || "modern" } : EMPTY;
        setForm(seed);
        setTab("general");
    }, [open, item]);

    // Debounced live preview re-render whenever form changes
    useEffect(() => {
        if (!open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => renderPreview(), 600);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [form, open]); // eslint-disable-line

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const importFromPdf = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("PDF only");
        if (file.size > 10 * 1024 * 1024) return toast.error("Max 10 MB");
        setImporting(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const token = localStorage.getItem("vyastra_token");
            const res = await fetch(`${API}/invoice-templates/inspect-pdf`, {
                method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
            });
            if (!res.ok) {
                const e = await res.json().catch(() => ({}));
                throw new Error(e.detail || "Import failed");
            }
            const { suggested, org_info } = await res.json();
            setForm((f) => ({
                ...f,
                ...Object.fromEntries(Object.entries(suggested).filter(([_, v]) => v !== null && v !== undefined && v !== "")),
            }));
            toast.success(`Extracted ${suggested.primary_color} + ${suggested.accent_color}, style: ${suggested.template_style || suggested.header_style}`);
            const orgKeys = Object.keys(org_info || {}).filter((k) => org_info[k]);
            if (orgKeys.length > 0) {
                toast(`Found ${orgKeys.length} org details (GSTIN, address, phone…) in this PDF`, {
                    description: `Click "Apply" to populate your Organisation profile. Detected: ${orgKeys.join(", ")}`,
                    duration: 8000,
                    action: {
                        label: "Apply to org",
                        onClick: async () => {
                            try {
                                const r = await api.post("/invoice-templates/apply-org-info", org_info);
                                toast.success(`Updated org profile: ${r.data.applied.join(", ")}`);
                            } catch (e) {
                                toast.error(e?.response?.data?.detail || "Failed to apply");
                            }
                        },
                    },
                });
            }
        } catch (e) {
            toast.error(e.message);
        } finally {
            setImporting(false);
            if (importInputRef.current) importInputRef.current.value = "";
        }
    };

    const renderPreview = async () => {
        setPreviewBusy(true);
        try {
            const token = localStorage.getItem("vyastra_token");
            const res = await fetch(`${API}/invoice-templates/preview-draft`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(form),
            });
            if (!res.ok) return;
            const blob = await res.blob();
            if (lastUrlRef.current) URL.revokeObjectURL(lastUrlRef.current);
            const url = URL.createObjectURL(blob);
            lastUrlRef.current = url;
            setPreviewUrl(url + "#toolbar=0&navpanes=0&view=FitH");
        } finally { setPreviewBusy(false); }
    };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Template needs a name");
        setSaving(true);
        try {
            // Mirror header_style → template_style for backwards compatibility
            const payload = { ...form, template_style: form.header_style || form.template_style || "modern" };
            if (item) await api.put(`/invoice-templates/${item.id}`, payload);
            else await api.post("/invoice-templates", payload);
            toast.success(item ? "Template updated" : "Template created");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="w-screen sm:max-w-[1280px] p-0 flex flex-col" data-testid="template-designer">
                {/* a11y: hidden title + description for Radix screen-reader compliance */}
                <SheetTitle className="sr-only">{item ? "Edit invoice template" : "New invoice template"}</SheetTitle>
                <SheetDescription className="sr-only">Configure paper, header, columns, totals and other PDF options. Live preview on the right.</SheetDescription>
                {/* Toolbar */}
                <div className="border-b border-border px-6 py-3 flex items-center justify-between bg-card">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-gold font-semibold">{item ? "EDIT TEMPLATE" : "NEW TEMPLATE"}</div>
                        <div className="font-display text-xl font-light tracking-tight">{form.name || "Untitled template"}</div>
                    </div>
                    <div className="flex gap-2 items-center">
                        <select value={form.header_style} onChange={(e) => set({ header_style: e.target.value, template_style: e.target.value })}
                                className="text-[12px] border border-border rounded-md px-2 py-1.5 bg-background" data-testid="designer-color-theme-quickstyle"
                                title="Quick style switcher">
                            {STYLES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                        <input ref={importInputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => importFromPdf(e.target.files?.[0])} data-testid="designer-import-input" />
                        <Button variant="outline" size="sm" onClick={() => importInputRef.current?.click()} disabled={importing} data-testid="designer-import-pdf" title="Upload an existing invoice PDF — Vyaahar will extract its colors and footer to seed this template">
                            {importing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}Import from PDF
                        </Button>
                        <Button variant="outline" size="sm" onClick={renderPreview} disabled={previewBusy} data-testid="designer-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${previewBusy ? "animate-spin" : ""}`} />Refresh Preview</Button>
                        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
                        <Button size="sm" onClick={save} disabled={saving} data-testid="template-save">{saving ? "Saving…" : (item ? "Save changes" : "Create template")}</Button>
                    </div>
                </div>

                {/* Layout: tab strip | controls | preview */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Tab rail */}
                    <div className="w-[88px] border-r border-border bg-muted/20 flex flex-col py-3">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.v;
                            return (
                                <button key={t.v} onClick={() => setTab(t.v)}
                                    data-testid={`designer-tab-${t.v}`}
                                    className={`w-full px-1 py-3 flex flex-col items-center gap-1 text-[10px] uppercase tracking-[0.06em] transition border-l-2 ${active ? "bg-card border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-card/50"}`}>
                                    <Icon className="w-4 h-4" />
                                    <span className="leading-tight text-center">{t.l}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Controls panel */}
                    <div className="w-[400px] border-r border-border overflow-y-auto p-5 bg-card">
                        {tab === "general" && <GeneralTab form={form} set={set} />}
                        {tab === "header" && <HeaderTab form={form} set={set} />}
                        {tab === "transaction" && <TransactionTab form={form} set={set} />}
                        {tab === "table" && <TableTab form={form} set={set} />}
                        {tab === "totals" && <TotalsTab form={form} set={set} />}
                        {tab === "other" && <OtherTab form={form} set={set} />}
                    </div>

                    {/* Live preview */}
                    <div className="flex-1 bg-muted/40 overflow-y-auto p-4 relative">
                        {previewBusy && (
                            <div className="absolute top-2 right-4 z-10 px-2 py-1 rounded-full bg-card border border-border text-[10px] flex items-center gap-1.5 shadow">
                                <Loader2 className="w-3 h-3 animate-spin" />Re-rendering…
                            </div>
                        )}
                        {previewUrl ? (
                            <iframe src={previewUrl} title="Live preview" className="w-full h-full bg-white rounded-md shadow-lg border border-border" style={{ minHeight: "calc(100vh - 120px)" }} data-testid="designer-preview" />
                        ) : (
                            <div className="h-full flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating preview…</div>
                        )}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

/* ============== TABS ============== */

function GeneralTab({ form, set }) {
    return (
        <div className="space-y-5">
            <Section title="Template name">
                <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Premium Quarterly" data-testid="designer-name" />
            </Section>

            <Section title="Color theme">
                <div className="flex flex-wrap gap-1.5">
                    {PALETTES.map((p) => {
                        const active = form.primary_color === p.primary && form.accent_color === p.accent;
                        return (
                            <button key={p.name} type="button" onClick={() => set({ primary_color: p.primary, accent_color: p.accent, header_bar_color: p.primary })}
                                className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full border transition ${active ? "border-foreground" : "border-border hover:border-foreground/40"}`}
                                data-testid={`designer-palette-${p.name.toLowerCase().replace(/\W/g, "-")}`} title={`${p.primary} / ${p.accent}`}>
                                <span className="flex">
                                    <span className="w-3.5 h-3.5 rounded-full border border-white/40" style={{ background: p.primary }} />
                                    <span className="w-3.5 h-3.5 rounded-full border border-white/40 -ml-1.5" style={{ background: p.accent }} />
                                </span>
                                <span className="text-[11px] font-medium">{p.name}</span>
                            </button>
                        );
                    })}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                    <ColorField label="Primary" value={form.primary_color} onChange={(v) => set({ primary_color: v })} testid="designer-primary" />
                    <ColorField label="Accent" value={form.accent_color} onChange={(v) => set({ accent_color: v })} testid="designer-accent" />
                </div>
            </Section>

            <Section title="Paper size">
                <div className="flex gap-1.5">
                    {PAPER_SIZES.map((p) => (
                        <Pill key={p} active={form.paper_size === p} onClick={() => set({ paper_size: p })} testid={`designer-paper-${p.toLowerCase()}`}>{p}</Pill>
                    ))}
                </div>
            </Section>

            <Section title="Orientation">
                <div className="flex gap-1.5">
                    <Pill active={form.orientation === "portrait"} onClick={() => set({ orientation: "portrait" })} testid="designer-orient-p">Portrait</Pill>
                    <Pill active={form.orientation === "landscape"} onClick={() => set({ orientation: "landscape" })} testid="designer-orient-l">Landscape</Pill>
                </div>
            </Section>

            <Section title="Margins (mm)">
                <div className="grid grid-cols-4 gap-2">
                    <NumField label="Top" v={form.margin_top} onChange={(v) => set({ margin_top: v })} testid="margin-top" />
                    <NumField label="Bottom" v={form.margin_bottom} onChange={(v) => set({ margin_bottom: v })} testid="margin-bottom" />
                    <NumField label="Left" v={form.margin_left} onChange={(v) => set({ margin_left: v })} testid="margin-left" />
                    <NumField label="Right" v={form.margin_right} onChange={(v) => set({ margin_right: v })} testid="margin-right" />
                </div>
            </Section>

            <Section title="Font">
                <select value={form.font_family} onChange={(e) => set({ font_family: e.target.value })}
                    className="w-full text-[12.5px] border border-border rounded-md px-2 py-2 bg-background" data-testid="designer-font-family">
                    {FONT_FAMILIES.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
                </select>
                <div className="mt-2">
                    <NumField label="Body font size (pt)" v={form.body_font_size} step="0.5" onChange={(v) => set({ body_font_size: v })} testid="font-size" />
                </div>
            </Section>

            <Section title="Background color (optional)">
                <ColorField label="Page background" value={form.bg_color || "#ffffff"} onChange={(v) => set({ bg_color: v === "#ffffff" ? "" : v })} testid="designer-bg" />
                <p className="text-[10.5px] text-muted-foreground mt-1">Leave white for plain. Use light tints (#fafafa) for branded paper.</p>
            </Section>
        </div>
    );
}

function HeaderTab({ form, set }) {
    return (
        <div className="space-y-5">
            <Section title="Header layout">
                <div className="grid grid-cols-1 gap-1.5">
                    {STYLES.map((s) => {
                        const active = form.header_style === s.v;
                        return (
                            <button key={s.v} type="button" onClick={() => set({ header_style: s.v, template_style: s.v })}
                                className={`text-left p-2.5 rounded-md border transition ${active ? "border-foreground bg-muted/30" : "border-border hover:border-foreground/40"}`}
                                data-testid={`designer-style-${s.v}`}>
                                <div className="flex items-center gap-1.5">
                                    <span className={`w-2.5 h-2.5 rounded-full ${active ? "bg-foreground" : "border-2 border-muted-foreground"}`} />
                                    <span className="font-semibold text-[12.5px]">{s.l}</span>
                                </div>
                                <p className="text-[10.5px] text-muted-foreground mt-1 leading-snug">{s.d}</p>
                            </button>
                        );
                    })}
                </div>
            </Section>

            <Section title="Title text">
                <Input value={form.header_title} onChange={(e) => set({ header_title: e.target.value })} placeholder="TAX INVOICE" data-testid="designer-header-title" />
                <div className="flex gap-1.5 mt-2">
                    {["left", "center", "right"].map((a) => (
                        <Pill key={a} active={form.header_title_align === a} onClick={() => set({ header_title_align: a })} testid={`title-align-${a}`}>{a[0].toUpperCase() + a.slice(1)}</Pill>
                    ))}
                </div>
            </Section>

            {form.header_style === "header_bar" && (
                <Section title="Banner colors">
                    <div className="grid grid-cols-2 gap-3">
                        <ColorField label="Banner" value={form.header_bar_color} onChange={(v) => set({ header_bar_color: v })} testid="banner-color" />
                        <ColorField label="Text" value={form.header_text_color} onChange={(v) => set({ header_text_color: v })} testid="banner-text-color" />
                    </div>
                </Section>
            )}

            <Section title="Logo">
                <CheckRow label="Show company logo" v={form.show_logo} onChange={(v) => set({ show_logo: v })} testid="designer-show-logo" />
                {form.show_logo && (
                    <>
                        <div className="flex gap-1.5 mt-2">
                            {["left", "center", "right"].map((a) => (
                                <Pill key={a} active={form.logo_position === a} onClick={() => set({ logo_position: a })} testid={`logo-pos-${a}`}>{a[0].toUpperCase() + a.slice(1)}</Pill>
                            ))}
                        </div>
                        <div className="mt-2">
                            <NumField label="Max width (mm)" v={form.logo_size_mm} onChange={(v) => set({ logo_size_mm: v })} testid="logo-size" />
                        </div>
                    </>
                )}
            </Section>

            <Section title="Org details in header">
                <CheckRow label="Show org name, address, GSTIN, phone" v={form.show_org_in_header} onChange={(v) => set({ show_org_in_header: v })} testid="show-org-header" />
            </Section>

            <Section title="Footer">
                <Input value={form.footer_text} onChange={(e) => set({ footer_text: e.target.value })} placeholder="e.g. Thank you for your business" data-testid="designer-footer" />
                <div className="mt-2">
                    <CheckRow label="Show page numbers" v={form.show_page_numbers} onChange={(v) => set({ show_page_numbers: v })} testid="show-page-numbers" />
                </div>
            </Section>
        </div>
    );
}

function TransactionTab({ form, set }) {
    return (
        <div className="space-y-5">
            <Section title="Field labels">
                <p className="text-[11px] text-muted-foreground mb-2">Customize the labels that appear in the right-side meta block.</p>
                <div className="grid grid-cols-1 gap-2.5">
                    <LabelInput label="Invoice number label" v={form.label_invoice_no} onChange={(v) => set({ label_invoice_no: v })} testid="lbl-invno" />
                    <LabelInput label="Invoice date label" v={form.label_invoice_date} onChange={(v) => set({ label_invoice_date: v })} testid="lbl-invdate" />
                    <LabelInput label="Due date label" v={form.label_due_date} onChange={(v) => set({ label_due_date: v })} testid="lbl-duedate" />
                    <LabelInput label="Terms label" v={form.label_terms} onChange={(v) => set({ label_terms: v })} testid="lbl-terms" />
                    <LabelInput label="Bill-To header" v={form.label_bill_to} onChange={(v) => set({ label_bill_to: v })} testid="lbl-billto" />
                    <LabelInput label="Ship-To header" v={form.label_ship_to} onChange={(v) => set({ label_ship_to: v })} testid="lbl-shipto" />
                    <LabelInput label="Balance Due card label" v={form.label_balance_due} onChange={(v) => set({ label_balance_due: v })} testid="lbl-baldue" />
                </div>
            </Section>
        </div>
    );
}

function TableTab({ form, set }) {
    return (
        <div className="space-y-5">
            <Section title="Visible columns">
                <CheckRow label="HSN/SAC" v={form.show_col_hsn} onChange={(v) => set({ show_col_hsn: v })} testid="col-hsn" />
                <CheckRow label="Quantity" v={form.show_col_qty} onChange={(v) => set({ show_col_qty: v })} testid="col-qty" />
                <CheckRow label="Rate" v={form.show_col_rate} onChange={(v) => set({ show_col_rate: v })} testid="col-rate" />
                <CheckRow label="Tax (CGST/SGST/IGST)" v={form.show_col_tax} onChange={(v) => set({ show_col_tax: v })} testid="col-tax" />
                <CheckRow label="Discount column" v={form.show_col_discount} onChange={(v) => set({ show_col_discount: v })} testid="col-disc" />
            </Section>
            <Section title="Row presentation">
                <CheckRow label="Show item description below name" v={form.show_description} onChange={(v) => set({ show_description: v })} testid="show-desc" />
                <CheckRow label="Alternating row backgrounds (zebra)" v={form.alternating_rows} onChange={(v) => set({ alternating_rows: v })} testid="alt-rows" />
            </Section>
        </div>
    );
}

function TotalsTab({ form, set }) {
    return (
        <div className="space-y-5">
            <Section title="Totals to display">
                <CheckRow label="Sub Total" v={form.show_total_subtotal} onChange={(v) => set({ show_total_subtotal: v })} testid="tot-sub" />
                <CheckRow label="Discount" v={form.show_total_discount} onChange={(v) => set({ show_total_discount: v })} testid="tot-disc" />
                <CheckRow label="Taxes (CGST / SGST / IGST)" v={form.show_total_taxes} onChange={(v) => set({ show_total_taxes: v })} testid="tot-tax" />
                <CheckRow label="Shipping" v={form.show_total_shipping} onChange={(v) => set({ show_total_shipping: v })} testid="tot-ship" />
                <CheckRow label="Round Off" v={form.show_total_roundoff} onChange={(v) => set({ show_total_roundoff: v })} testid="tot-round" />
                <CheckRow label="Payment Made" v={form.show_total_paid} onChange={(v) => set({ show_total_paid: v })} testid="tot-paid" />
            </Section>
            <Section title="Custom labels">
                <div className="grid grid-cols-1 gap-2.5">
                    <LabelInput label="Sub Total label" v={form.label_subtotal} onChange={(v) => set({ label_subtotal: v })} testid="lbl-sub" />
                    <LabelInput label="Total label" v={form.label_total} onChange={(v) => set({ label_total: v })} testid="lbl-total" />
                </div>
            </Section>
            <Section title="Amount in words">
                <CheckRow label="Show 'Total in Words' line" v={form.show_amount_in_words} onChange={(v) => set({ show_amount_in_words: v })} testid="show-aiw" />
            </Section>
        </div>
    );
}

function OtherTab({ form, set }) {
    return (
        <div className="space-y-5">
            <Section title="Notes & Terms">
                <CheckRow label="Show invoice notes" v={form.show_notes} onChange={(v) => set({ show_notes: v })} testid="show-notes" />
                <CheckRow label="Show terms & conditions" v={form.show_terms} onChange={(v) => set({ show_terms: v })} testid="show-terms" />
                <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground mt-2 block">Default Terms & Conditions</Label>
                <textarea className="w-full border border-border rounded-md p-2 text-[12.5px] mt-1.5 min-h-[100px] font-sans"
                    value={form.terms_template} onChange={(e) => set({ terms_template: e.target.value })} data-testid="designer-terms" />
            </Section>

            <Section title="Signature">
                <CheckRow label="Show signature block" v={form.show_signature} onChange={(v) => set({ show_signature: v })} testid="show-sig" />
                {form.show_signature && (
                    <LabelInput label="Signature label" v={form.signature_label} onChange={(v) => set({ signature_label: v })} testid="signature-label" />
                )}
            </Section>

            <Section title="Bank details (printed on invoice)">
                <CheckRow label="Show bank details block" v={form.show_bank_details} onChange={(v) => set({ show_bank_details: v })} testid="show-bank" />
                {form.show_bank_details && (
                    <textarea className="w-full border border-border rounded-md p-2 text-[12.5px] mt-1.5 min-h-[100px] font-sans"
                        value={form.bank_details} onChange={(e) => set({ bank_details: e.target.value })}
                        placeholder={`HDFC Bank · A/c 1234567890\nIFSC HDFC0000001\nBranch: Chennai`}
                        data-testid="designer-bank" />
                )}
            </Section>

            <Section title="E-Invoice IRN block">
                <CheckRow label="Show E-invoice IRN/Ack block (when present)" v={form.show_e_invoice_block} onChange={(v) => set({ show_e_invoice_block: v })} testid="show-einv" />
            </Section>

            <label className="flex items-center gap-2 text-[12.5px] pt-3 border-t border-border cursor-pointer">
                <input type="checkbox" checked={form.set_as_default} onChange={(e) => set({ set_as_default: e.target.checked })} data-testid="designer-default" />
                <span>Set as default for all new invoices</span>
            </label>
        </div>
    );
}

/* ============== reusable bits ============== */

function Section({ title, children }) {
    return (
        <div>
            <Label className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">{title}</Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}

function ColorField({ label, value, onChange, testid }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</Label>
            <div className="flex gap-2 mt-1">
                <input type="color" value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)} className="h-9 w-10 rounded-md border border-border cursor-pointer" data-testid={`${testid}-swatch`} />
                <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="font-mono text-[11.5px] h-9" data-testid={testid} />
            </div>
        </div>
    );
}

function NumField({ label, v, onChange, testid, step = "1" }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</Label>
            <Input type="number" step={step} value={v} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="h-9 mt-1" data-testid={`designer-${testid}`} />
        </div>
    );
}

function LabelInput({ label, v, onChange, testid }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">{label}</Label>
            <Input value={v} onChange={(e) => onChange(e.target.value)} className="h-9 mt-1" data-testid={`designer-${testid}`} />
        </div>
    );
}

function Pill({ active, onClick, children, testid }) {
    return (
        <button type="button" onClick={onClick}
            className={`px-3 py-1.5 rounded-full border text-[11.5px] transition ${active ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground/50"}`}
            data-testid={`designer-${testid}`}>{children}</button>
    );
}

function CheckRow({ label, v, onChange, testid }) {
    return (
        <label className="flex items-center gap-2 text-[12.5px] cursor-pointer py-1">
            <input type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)} data-testid={`designer-${testid}`} />
            <span>{label}</span>
        </label>
    );
}
